import { StatusCodes, useApiCreate } from '@/composables/api'
import { Post } from '@/model/data-object/Post'
import { PostVote, Vote } from '@/model/data-object/PostVote'
import useCurrentUser from '@/composables/user/user'
import NotLoggedIn from '@/error/notLoggedIn'
import { VoteRes } from '@/model/data-object/VoteRes'
import { ref, Ref } from 'vue'
import { ApiTimeout } from '@/composables/apiTimeout'
import { TOOLTIP_COMPONENTS } from '@/composables/post/postTooltip'
import useAnalyticsManager, {
  ANALYTICS_EVENT_POST_VOTE_DISLIKE,
  ANALYTICS_EVENT_POST_VOTE_LIKE,
} from '@/composables/analytics/analyticsManager'

const TIMEOUT_DOMAIN_POSITIVE = 'forum_widget_post_vote_pos_'
const TIMEOUT_DOMAIN_NEGATIVE = 'forum_widget_post_vote_neg_'

export default function usePostVote(
  currentComponent: Ref<string | undefined>,
  alreadyPerformed: Ref<boolean>,
  changed: Ref<boolean>
) {
  const isVotingPositive: Ref<boolean> = ref(false)
  const isVotingNegative: Ref<boolean> = ref(false)
  let votedPositive = false
  let votedNegative = false
  const voted: Ref<boolean> = ref(false)
  const { getUserId, loggedIn } = useCurrentUser()

  const voteOnPost = async (
    post: Post,
    vote: PostVote,
    timeout: ApiTimeout,
    isVoting: Ref<boolean>
  ): Promise<void> => {
    const { response, request, statusCode } = useApiCreate<VoteRes>(
      '/api/prv/v1/postVote',
      vote,
      {},
      timeout
    )
    isVoting.value = true
    await request()
    isVoting.value = false
    voted.value = true

    currentComponent.value = TOOLTIP_COMPONENTS.POST_VOTE
    if (statusCode.value === StatusCodes.OK) {
      alreadyPerformed.value = true
    } else if (statusCode.value === StatusCodes.CREATED) {
      alreadyPerformed.value = false
    }

    if (response.value) {
      post.stats = response.value.stats
      changed.value = response.value.voteChange
    }

    return
  }

  const voteLoginTooltip = () => {
    if (!loggedIn.value) {
      if (currentComponent.value === TOOLTIP_COMPONENTS.POST_VOTE_LOGIN) {
        currentComponent.value = undefined

        return true
      }
      currentComponent.value = TOOLTIP_COMPONENTS.POST_VOTE_LOGIN

      return true
    }

    return false
  }

  const votePositive = (post: Post): Promise<void> | undefined => {
    if (voteLoginTooltip()) {
      return
    }
    if (
      currentComponent.value === TOOLTIP_COMPONENTS.POST_VOTE &&
      votedPositive
    ) {
      currentComponent.value = undefined

      return
    }
    if (loggedIn.value) {
      const { propagateAnalyticsEvent } = useAnalyticsManager()
      propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_VOTE_LIKE)
      votedPositive = true
      votedNegative = false
      return voteOnPost(
        post,
        new PostVote(post.id, getUserId(), Vote.positive),
        new ApiTimeout(TIMEOUT_DOMAIN_POSITIVE + post.id, false),
        isVotingPositive
      )
    }
    throw new NotLoggedIn('postVote')
  }

  const voteNegative = (post: Post): Promise<void> | undefined => {
    if (voteLoginTooltip()) {
      return
    }
    if (
      currentComponent.value === TOOLTIP_COMPONENTS.POST_VOTE &&
      votedNegative
    ) {
      currentComponent.value = undefined

      return
    }
    if (loggedIn.value) {
      const { propagateAnalyticsEvent } = useAnalyticsManager()
      propagateAnalyticsEvent(post, ANALYTICS_EVENT_POST_VOTE_DISLIKE)
      votedPositive = false
      votedNegative = true
      return voteOnPost(
        post,
        new PostVote(post.id, getUserId(), Vote.negative),
        new ApiTimeout(TIMEOUT_DOMAIN_NEGATIVE + post.id, false),
        isVotingNegative
      )
    }
    throw new NotLoggedIn('postVote')
  }

  return {
    isVotingNegative,
    isVotingPositive,
    voted,
    voteOnPost,
    votePositive,
    voteNegative,
  }
}
